import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { ADMIN_BASE } from "../../config";
import InstallButton from "../InstallButton";
import {
    LayoutDashboard,
    Newspaper,
    CalendarDays,
    GraduationCap,
    Inbox,
    Users,
    History,
    BarChart3,
    LogOut,
    ExternalLink,
    Sparkles,
    Share2,
    PenLine,
    Heart,
} from "lucide-react";

const LINKS = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/compose", label: "Create Post", icon: Sparkles },
    { to: "/admin/ai-writer", label: "AI Writer", icon: PenLine },
    { to: "/admin/channels", label: "Channels", icon: Share2 },
    { to: "/admin/blog", label: "Blog", icon: Newspaper },
    { to: "/admin/events", label: "Events", icon: CalendarDays },
    { to: "/admin/trainings", label: "Trainings", icon: GraduationCap },
    { to: "/admin/messages", label: "Messages", icon: Inbox, ownerOnly: true },
    { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
    { to: "/admin/access", label: "Access", icon: Users },
    { to: "/admin/activity", label: "History", icon: History },
];

export default function AdminLayout() {
    const { admin, logout } = useAuth();
    const navigate = useNavigate();
    const links = LINKS.filter((l) => !l.ownerOnly || admin?.is_owner);

    const doLogout = () => {
        logout();
        navigate(ADMIN_BASE);
    };

    return (
        <div className="flex min-h-screen bg-[#070c18] text-blue-50">
            <aside className="fixed inset-y-0 left-0 flex w-60 flex-col border-r border-blue-500/15 bg-[#0c172e] trillian-panel p-4">
                <Link to="/" className="flex items-center gap-2.5 px-2 py-2">
                    <span className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-b from-[#1e3a8a] to-[#0a1326] ring-1 ring-blue-400/40">
                        <img
                            src="/images/sprite-neuro.png"
                            alt="Professor Neuro"
                            className="h-7 w-7 rounded-lg object-cover neuro-float"
                        />
                        <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 text-emerald-400 led-pulse ring-2 ring-[#0c172e]" />
                    </span>
                    <span className="flex flex-col leading-tight">
                        <span className="font-serif text-lg text-blue-50">Control Panel</span>
                        <span className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.2em] text-sky-400/70">
                            <span className="h-1.5 w-1.5 rounded-full bg-rose-400" />
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-300" />
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                            online
                        </span>
                    </span>
                </Link>

                <nav className="mt-6 flex flex-1 flex-col gap-1">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.end}
                            data-testid={`admin-nav-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                                    isActive
                                        ? "bg-blue-500/15 text-slate-400"
                                        : "text-slate-300/65 hover:bg-blue-500/10 hover:text-slate-300"
                                }`
                            }
                        >
                            <l.icon className="h-4 w-4" />
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="mt-4 border-t border-blue-500/15 pt-4">
                    <InstallButton variant="admin" />
                    <a
                        href="/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-300/60 hover:text-slate-400"
                    >
                        <ExternalLink className="h-4 w-4" /> View site
                    </a>
                    <div className="px-3 py-2 text-xs text-slate-300/40">
                        {admin?.email}
                    </div>
                    <button
                        data-testid="admin-logout"
                        onClick={doLogout}
                        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-red-300/80 hover:bg-red-500/10 hover:text-red-200"
                    >
                        <LogOut className="h-4 w-4" /> Log out
                    </button>

                    <div
                        data-testid="admin-credit"
                        className="mt-3 flex items-center justify-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-sky-400/50"
                    >
                        coded with
                        <Heart className="h-3 w-3 fill-rose-400/70 text-rose-400/70" />
                        by
                        <span className="rounded-md bg-blue-500/15 px-1.5 py-0.5 font-semibold tracking-[0.15em] text-sky-300 ring-1 ring-blue-400/30">
                            E1
                        </span>
                    </div>
                </div>
            </aside>

            <main className="ml-60 flex-1 px-8 py-8">
                <Outlet />
            </main>
        </div>
    );
}
